import { NavLink, Outlet, Link, useLocation } from "react-router-dom";
import { useState, useEffect } from "react";
import { Menu, X, Instagram as InstagramIcon, Loader2 } from "lucide-react";
import { InkFlourish } from "./Ink";
import { api, resolveMedia } from "../lib/api";
import Chatbot from "./Chatbot";
import { useI18n } from "../i18n";

const navItems = [
  { to: "/", label: "Journal", end: true },
  { to: "/calendario", label: "Calendario" },
  { to: "/experiencias", label: "Experiencias" },
  { to: "/concierge", label: "Concierge" },
];

function LangPill() {
  const { lang, setLang, loading } = useI18n();
  const btn = (code, label) => {
    const active = lang === code;
    return (
      <button
        type="button"
        onClick={() => setLang(code)}
        aria-pressed={active}
        className={`px-2.5 py-1 text-[0.62rem] tracking-[0.28em] uppercase font-display font-semibold transition ${
          active ? "text-cobalt" : "text-subtle hover:text-ink"
        }`}
        data-testid={`lang-toggle-${code}`}
        data-no-translate="true"
      >
        {label}
      </button>
    );
  };
  return (
    <div
      className="inline-flex items-center border border-cobalt/25 rounded-full overflow-hidden bg-white/80"
      data-testid="lang-toggle"
      data-no-translate="true"
    >
      {btn("es", "ES")}
      <span className="w-px h-3 bg-cobalt/25" />
      {btn("en", "EN")}
      {loading && (
        <span className="pr-2 pl-1 text-cobalt" aria-live="polite">
          <Loader2 size={11} className="animate-spin" />
        </span>
      )}
    </div>
  );
}

export default function Layout() {
  const [open, setOpen] = useState(false);
  const [igItems, setIgItems] = useState([]);
  const location = useLocation();
  const hideChatbot = location.pathname.startsWith("/admin");
  const isAdmin = location.pathname.startsWith("/admin");

  useEffect(() => {
    api.get("/site/instagram").then((r) => setIgItems(r.data?.items || [])).catch(() => {});
  }, []);

  return (
    <div className="min-h-screen bg-white text-ink flex flex-col" data-no-translate={isAdmin ? "true" : "false"}>
      {/* Brand masthead — white */}
      <div className="hidden md:block bg-white border-b border-black/10">
        <div className="max-w-7xl mx-auto px-6 sm:px-10 lg:px-16 pt-8 pb-5 flex flex-col items-center">
          <p className="font-display text-[0.6rem] tracking-[0.5em] uppercase text-cobalt font-semibold">Est. 2024 · Boletín Privado</p>
          <Link to="/" className="mt-3 text-cobalt-deep" data-testid="nav-logo" data-no-translate="true">
            <span className="font-serif text-[2.9rem] leading-none tracking-tight">Valle de Bravo <span className="serif-italic text-cobalt">Insider</span></span>
          </Link>
          <InkFlourish className="w-36 h-5 mt-4 opacity-70" stroke="#1F3A6E" />
        </div>
      </div>

      {/* Sticky nav */}
      <header className="sticky top-0 z-40 bg-white/95 backdrop-blur border-b border-black/10">
        <div className="max-w-7xl mx-auto px-6 sm:px-10 lg:px-16 h-14 flex items-center justify-between gap-6">
          <Link to="/" className="md:hidden font-serif text-lg leading-none text-cobalt-deep" data-testid="nav-logo-mobile" data-no-translate="true">
            Valle de Bravo <span className="serif-italic text-cobalt">Insider</span>
          </Link>
          <nav className="hidden md:flex items-center gap-14 mx-auto">
            {navItems.map((n) => (
              <NavLink
                key={n.to}
                to={n.to}
                end={n.end}
                data-testid={`nav-link-${n.label.toLowerCase()}`}
                className={({ isActive }) => `nav-link ${isActive ? "active" : ""}`}
              >
                {n.label}
              </NavLink>
            ))}
          </nav>
          <div className="hidden md:block">
            <LangPill />
          </div>
          <button
            className="md:hidden p-2 text-cobalt-deep"
            onClick={() => setOpen((v) => !v)}
            data-testid="nav-mobile-toggle"
            aria-label="Menú"
          >
            {open ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>

        {open && (
          <div className="md:hidden border-t border-black/10 bg-white">
            <div className="px-6 py-6 flex flex-col gap-5">
              {navItems.map((n) => (
                <NavLink
                  key={n.to}
                  to={n.to}
                  end={n.end}
                  onClick={() => setOpen(false)}
                  data-testid={`nav-mobile-${n.label.toLowerCase()}`}
                  className={({ isActive }) => `nav-link ${isActive ? "active" : ""}`}
                >
                  {n.label}
                </NavLink>
              ))}
              <Link to="/proponer" onClick={() => setOpen(false)} className="nav-link" data-testid="nav-mobile-proponer">Proponer un evento</Link>
              <div className="pt-3 border-t border-black/10">
                <LangPill />
              </div>
            </div>
          </div>
        )}
      </header>

      <main className="flex-1">
        <Outlet />
      </main>

      {/* Instagram strip — only when admin has configured items */}
      {igItems.length > 0 && (
        <section className="bg-white border-t border-black/10 py-12" data-testid="instagram-strip">
          <div className="max-w-7xl mx-auto px-6 sm:px-10 lg:px-16">
            <div className="flex items-end justify-between mb-6 flex-wrap gap-4">
              <div>
                <p className="eyebrow">Desde Instagram</p>
                <h3 className="mt-2 font-serif text-2xl text-cobalt-deep">@valledebravoinsider</h3>
              </div>
              <a
                href="https://instagram.com/valledebravoinsider"
                target="_blank"
                rel="noopener noreferrer"
                className="text-[0.68rem] tracking-[0.26em] uppercase text-cobalt border-b border-cobalt pb-1 font-display font-semibold hover:text-terracotta hover:border-terracotta inline-flex items-center gap-2"
                data-testid="instagram-follow"
              >
                <InstagramIcon size={14}/> Seguir
              </a>
            </div>
            <div className="ig-strip flex gap-4 -mx-6 px-6 sm:-mx-10 sm:px-10 lg:-mx-16 lg:px-16" data-testid="instagram-gallery">
              {igItems.map((it, i) => {
                const inner = (
                  <>
                    <img
                      src={resolveMedia(it.image)}
                      alt={it.caption || `Instagram ${i + 1}`}
                      className="w-[220px] sm:w-[260px] aspect-square object-cover"
                    />
                    <div className="absolute inset-0 bg-cobalt/0 group-hover:bg-cobalt/35 transition-colors flex items-center justify-center">
                      <InstagramIcon size={28} className="text-white opacity-0 group-hover:opacity-100 transition-opacity" />
                    </div>
                  </>
                );
                const className = "relative shrink-0 overflow-hidden group block";
                return it.link ? (
                  <a
                    key={i}
                    href={it.link}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={className}
                    data-testid={`ig-item-${i}`}
                  >{inner}</a>
                ) : (
                  <div key={i} className={className} data-testid={`ig-item-${i}`}>{inner}</div>
                );
              })}
            </div>
          </div>
        </section>
      )}

      {/* Footer — full-bleed cobalt canvas */}
      <footer className="canvas-cobalt">
        <div className="max-w-7xl mx-auto px-6 sm:px-10 lg:px-16 py-20 grid gap-12 md:grid-cols-4 items-start">
          <div className="md:col-span-2">
            <p className="font-serif text-3xl" data-no-translate="true">Valle de Bravo <span className="serif-italic" style={{ color: "#E88A5A" }}>Insider</span></p>
            <p className="mt-4 text-sm text-white/80 leading-relaxed max-w-sm">
              Un boletín privado y un servicio de concierge para las familias que ya tienen casa en el Valle. Discreto, editado, sin prisa.
            </p>
            <InkFlourish className="w-32 h-5 mt-6 opacity-70" stroke="#E88A5A" />
          </div>
          <div>
            <p className="eyebrow-on-dark">Navegación</p>
            <ul className="mt-5 space-y-2 text-sm text-white">
              <li><Link to="/">Journal</Link></li>
              <li><Link to="/calendario">Calendario</Link></li>
              <li><Link to="/experiencias">Experiencias</Link></li>
              <li><Link to="/concierge">Concierge</Link></li>
              <li><Link to="/proponer" data-testid="footer-proponer">Proponer un evento</Link></li>
            </ul>
          </div>
          <div>
            <p className="eyebrow-on-dark">Contacto</p>
            <p className="mt-5 text-sm text-white/80 leading-relaxed">WhatsApp de 9:00 a 20:00. Respondemos en el día.</p>
            <a
              href="https://instagram.com/valledebravoinsider"
              target="_blank"
              rel="noopener noreferrer"
              className="mt-4 inline-flex items-center gap-2 text-sm text-white/80 hover:text-white"
              data-testid="footer-instagram"
            >
              <InstagramIcon size={14}/> @valledebravoinsider
            </a>
            <div className="mt-8 flex items-center justify-between gap-4">
              <p className="text-[0.6rem] text-white/50 tracking-[0.32em] uppercase font-display">© {new Date().getFullYear()}</p>
              <Link to="/admin" data-testid="footer-editor" className="text-[0.6rem] text-white/40 hover:text-white tracking-[0.32em] uppercase font-display">
                Editor
              </Link>
            </div>
          </div>
        </div>
      </footer>
      {!hideChatbot && <Chatbot />}
    </div>
  );
}
